<template>
  <div class="guide-recipient-types">
    <h2>What are recipient types?</h2>
    <p>
      Every participant in a signing package is assigned a <strong>type</strong> that
      defines their role in the transaction - what they can do, what they must do,
      and when they are notified. Some types sign documents, some review and approve,
      and others simply receive a copy when everything is complete.
    </p>
    <p>
      Choosing the right type for each participant ensures your signing workflow
      matches the real-world process. A contract might need signers, a manager's
      approval, and a compliance team CC'd on the result - each of those is a
      different recipient type.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Types at a glance</h2>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Purpose</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Action required</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Can decline</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Signer</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Primary document signer</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Must complete all assigned fields (signature, initials, text, etc.)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Approver</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Reviews and approves before signers proceed</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Clicks approve or deny - no fields to complete</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes (denial pauses the workflow)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Editor</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Fills in or modifies fields before other participants act</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Edits fields then submits - does not sign</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CertifiedDelivery</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Must acknowledge receipt of the documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Opens the documents and clicks acknowledge</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CarbonCopy</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Passive recipient - receives completed documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">None - no signing, no acknowledgement</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      If <code>type</code> is omitted when creating a signer, it defaults to
      <code>Signer</code>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Signer</h2>
    <p>
      The default and most common type. A Signer must complete all signing fields
      assigned to them - signatures, initials, text fields, checkboxes, dates, and
      so on. The package cannot reach <code>Completed</code> status until every
      Signer has finished their fields.
    </p>
    <p>
      Signers can decline the package. If any Signer declines, the entire package
      terminates with <code>Declined</code> status and all other participants are
      notified.
    </p>
    <p>
      <strong>Requires at least one annotation.</strong> A package will not pass
      validation if a Signer has no fields assigned to them. Every Signer must have
      something to complete.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Approver</h2>
    <p>
      An Approver reviews the package and either approves it (allowing the workflow
      to continue) or denies it. Approvers do not sign or complete fields - their
      action is a simple approve-or-deny gate.
    </p>
    <p>
      <strong>Denial is not terminal.</strong> Unlike a Signer decline (which ends
      the package), an Approver denial pauses the workflow. All Editors in the
      package are reset so they can make corrections and resubmit. Once the Editors
      resubmit, the Approver is notified again to re-review. This approve → deny →
      correct → re-approve cycle can repeat until the Approver is satisfied.
    </p>
    <p>
      <strong>Use case:</strong> A manager must approve a contract before it goes
      out for signature. A compliance officer must sign off on terms before the
      client sees the document.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Editor</h2>
    <p>
      An Editor can fill in or modify document fields before signers or approvers
      act. Editors do not sign - they prepare the document by entering data (amounts,
      dates, terms) and then submit their work for the next phase.
    </p>
    <p>
      Editors always act first in the workflow. If an Approver denies the package,
      all Editors are automatically reset so they can revise and resubmit.
    </p>
    <p>
      <strong>Use case:</strong> A loan officer fills in the loan amount, interest
      rate, and closing date before the borrower and seller sign. An HR
      administrator enters the salary and start date before the employee signs
      their offer letter.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Certified Delivery</h2>
    <p>
      A Certified Delivery recipient must open the documents and click an
      acknowledge button, creating an audit record that they received and viewed
      the materials. They do not sign or complete any fields.
    </p>
    <p>
      <strong>Use case:</strong> A compliance officer or witness who must confirm
      they received and reviewed the signed documents. A regulatory body that
      requires proof of document delivery.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Carbon Copy</h2>
    <p>
      A Carbon Copy (CC) recipient takes no action during the signing ceremony.
      They do not receive a signing invitation when the package is sent. Instead,
      they are automatically notified with download links to the completed
      documents once the package finishes.
    </p>
    <p>
      CC recipients cannot have annotations assigned to them and are excluded from
      routing order logic entirely. They exist purely to ensure the right people
      receive the final signed documents without participating in the ceremony.
    </p>
    <p>
      <strong>Use case:</strong> A legal department that needs the executed contract
      on file. An archive mailbox. A filing system or document management webhook
      endpoint that receives completed packages.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Execution order</h2>
    <p>
      Recipient types follow a fixed phase order that determines when each
      participant is notified, regardless of individual routing order values.
      The phases execute sequentially:
    </p>
    <ol>
      <li><strong>Editors</strong> act first - they prepare the document.</li>
      <li><strong>Approvers</strong> review and approve the Editor's work.</li>
      <li><strong>Signers</strong> complete their fields. This is the only phase
        where the <code>enforceSigningOrder</code> flag and
        <code>routingOrder</code> values apply. When sequential signing is enabled,
        signers within this phase proceed in routing-order groups.</li>
      <li><strong>Certified Delivery</strong> recipients acknowledge receipt.</li>
      <li><strong>Carbon Copy</strong> recipients are notified with the completed
        documents (no action required).</li>
    </ol>
    <p>
      This phased model means an Editor always acts before an Approver, and an
      Approver always acts before a Signer - even if you assign them the same
      routing order number. The <code>routingOrder</code> field only controls
      sequencing <em>within</em> the Signer phase.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Example:</strong> A package with an Editor (loan officer), an
      Approver (manager), two Signers (borrower at routing order 1, seller at
      routing order 2), and a CC (title company) will always flow in that exact
      sequence - Editor first, then Approver, then borrower, then seller, then
      the title company receives the completed documents.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Choosing the right type</h2>
    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Scenario</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Recommended type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Person who needs to sign the document</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Signer</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Standard signing with assigned fields</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manager or authority who must approve before others sign</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Approver</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Gate with non-terminal denial and correction loop</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Staff member who fills in data before others sign</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Editor</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Prepares document without adding a signature</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Person who must confirm they received the documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CertifiedDelivery</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Creates an auditable delivery receipt</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Team or mailbox that needs the completed documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>CarbonCopy</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Passive - no ceremony participation, no signing action</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h2>Multiple participants vs. signing groups</h2>
    <p>
      When a workflow has several people of the same type - for example, three
      Approvers or two Editors - the platform treats them differently depending
      on whether you add them as individual participants or assign a
      <a href="/Docs/IntegrationGuides?topic=signing-groups">signing group</a>
      to the slot. Understanding the distinction is important because it changes
      who must act and when the workflow advances.
    </p>

    <h3>Multiple individual participants (all-of)</h3>
    <p>
      When you add multiple people as separate participants of the same type,
      <strong>all of them</strong> must complete their part before the workflow
      moves to the next phase. They act in parallel - all are notified at the
      same time - but the phase does not advance until the last person finishes.
    </p>
    <ul>
      <li>
        <strong>Multiple Editors:</strong> All Editors are notified simultaneously
        and can work in parallel. The Approver phase does not begin until
        <em>every</em> Editor has submitted.
      </li>
      <li>
        <strong>Multiple Approvers:</strong> All Approvers are notified
        simultaneously. If <em>any one</em> Approver denies, the package is
        paused and Editors are reset for corrections - regardless of whether
        other Approvers have already approved. However, once one Approver
        completes approval, the workflow can advance.
      </li>
      <li>
        <strong>Multiple Signers:</strong> When <code>enforceSigningOrder</code>
        is enabled, Signers with the same <code>routingOrder</code> sign in
        parallel. <em>All</em> Signers in a routing-order group must complete
        before the next group is notified.
      </li>
    </ul>

    <h3>Signing groups (any-of)</h3>
    <p>
      A <a href="/Docs/IntegrationGuides?topic=signing-groups">signing group</a>
      assigns a roster of people to a single participant slot. All members of the
      group receive the signing notification, but <strong>only one member needs to
      act</strong>. Once any member completes the slot, the remaining members are
      blocked and the workflow advances.
    </p>
    <p>
      This is the key difference: individual participants use <strong>all-of</strong>
      semantics (everyone must act), while signing groups use
      <strong>any-of</strong> semantics (first to act wins).
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Example:</strong> A contract requires approval from the legal
      department. If you add three lawyers as individual Approvers, all three
      must approve before the workflow continues. If you create a "Legal
      Department" signing group with the same three lawyers and assign it to one
      Approver slot, the first lawyer to approve advances the workflow - the
      other two do not need to act.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Managing types in the portal</h2>
    <p>
      When adding a signer in the package builder, select the recipient type from
      the <strong>Type</strong> dropdown in the signer's settings panel. The
      default is <code>Signer</code>. The available options and their
      behaviour are the same as described above.
    </p>
    <p>
      For templates, set the type on each role in the template editor. When the
      template is used to create a package, the mapped signer inherits the role's
      type.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      The <code>type</code> field is set when creating or updating a signer via
      the API. It accepts a case-insensitive string matching one of the five values.
    </p>

    <h3>Adding signers with different types</h3>
    <p>
      Use the bulk signer endpoint to add a complete workflow in a single request.
      The example below creates an Editor → Approver → Signer → CC pipeline:
    </p>
    <GuideCodeTabs :samples="samples.bulkTypes" />

    <h3>Setting type on a template role</h3>
    <p>
      Template roles accept the same <code>type</code> values. The type is
      inherited by the concrete signer when the template is instantiated:
    </p>
    <GuideCodeTabs :samples="samples.templateRole" />

    <q-separator class="q-my-lg" />

    <h3>Type-specific behaviour reference</h3>
    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Behaviour</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Signer</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Approver</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Editor</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">CertifiedDelivery</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">CarbonCopy</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Receives invite on send</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes*</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes*</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Completes fields</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Edits only</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">N/A</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Can have annotations</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Optional</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Optional</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Annotations required</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">At least one</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">N/A</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Can decline</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes (terminal)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes (non-terminal)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes (terminal)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Execution phase</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">2</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">1</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">0</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">3</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">4 (auto)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Affected by <code>enforceSigningOrder</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Can have payment</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Yes</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      * Approvers and CertifiedDelivery recipients are invited when their phase
      begins (after preceding phases complete), not immediately on send.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Validation rules</h3>
    <ul>
      <li>
        A package must have at least one <strong>action participant</strong>
        (<code>Signer</code> or <code>Approver</code>).
        A package with only CarbonCopy recipients will not pass validation.
      </li>
      <li>
        <code>Signer</code> must have at least one annotation assigned. Other types
        do not require annotations.
      </li>
      <li>
        <code>CarbonCopy</code> recipients cannot have annotations assigned. The
        API returns an error if you attempt to place a field on a CC recipient.
      </li>
      <li>
        Duplicate <code>(email, type)</code> combinations are not allowed within
        the same package. However, the same email address can appear with different
        types - for example, one person can be both a <code>Signer</code> on one
        document and an <code>Approver</code> on the workflow.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>MCP tool equivalents</h3>
    <p>
      The <code>type</code> field is available on all signer and template role
      tools:
    </p>
    <ul>
      <li><code>add_signer</code> - set <code>type</code> when adding a signer to a package</li>
      <li><code>add_signers_bulk</code> - each signer object supports <code>type</code></li>
      <li><code>update_signer</code> - change type on a draft signer</li>
      <li><code>add_template_role</code> - set the role's <code>type</code> for template instantiation</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Use Editors for data entry, not Signers.</strong> If someone needs
        to fill in dates, amounts, or terms but should not add their signature, make
        them an Editor. This keeps the audit trail clear - only Signers produce
        legally binding signatures.
      </li>
      <li>
        <strong>Use Approvers for gates, not sequential signing.</strong> If you
        need a manager to approve before the client signs, use an Approver - not a
        Signer with routing order 1. Approvers have the deny-and-correct loop;
        Signers who decline terminate the entire package.
      </li>
      <li>
        <strong>Include CC recipients for compliance and record-keeping.</strong>
        Adding your legal team, compliance inbox, or document management system as
        CC ensures the right parties receive completed documents automatically
        without participating in the ceremony.
      </li>
      <li>
        <strong>Use templates to standardise role types.</strong> If your workflow
        always follows the same pattern (Editor → Approver → Signer → CC),
        define it once in a template. Each role's type, authentication method,
        and payment amount carry forward to every package created from the template.
      </li>
    </ul>

    <q-banner class="bg-green-1 text-green-9 q-mt-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Signers</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    // ─── Bulk add with different types (full workflow) ─────────────────────────
    bulkTypes: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers/bulk \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "signers": [
      { "name": "Loan Officer",  "email": "officer@acme.com",  "type": "Editor" },
      { "name": "Branch Manager","email": "manager@acme.com",  "type": "Approver" },
      { "name": "Jane Borrower", "email": "jane@example.com",  "type": "Signer",     "routingOrder": 1 },
      { "name": "John Seller",   "email": "john@example.com",  "type": "Signer",     "routingOrder": 2 },
      { "name": "Title Company", "email": "filing@titleco.com","type": "CarbonCopy" }
    ]
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    signers: [
      { name: 'Loan Officer',   email: 'officer@acme.com',   type: 'Editor' },
      { name: 'Branch Manager', email: 'manager@acme.com',   type: 'Approver' },
      { name: 'Jane Borrower',  email: 'jane@example.com',   type: 'Signer',     routingOrder: 1 },
      { name: 'John Seller',    email: 'john@example.com',   type: 'Signer',     routingOrder: 2 },
      { name: 'Title Company',  email: 'filing@titleco.com', type: 'CarbonCopy' }
    ]
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    signers: [
      { name: 'Loan Officer',   email: 'officer@acme.com',   type: 'Editor' },
      { name: 'Branch Manager', email: 'manager@acme.com',   type: 'Approver' },
      { name: 'Jane Borrower',  email: 'jane@example.com',   type: 'Signer',     routingOrder: 1 },
      { name: 'John Seller',    email: 'john@example.com',   type: 'Signer',     routingOrder: 2 },
      { name: 'Title Company',  email: 'filing@titleco.com', type: 'CarbonCopy' }
    ]
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers/bulk',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'signers': [
            {'name': 'Loan Officer',   'email': 'officer@acme.com',   'type': 'Editor'},
            {'name': 'Branch Manager', 'email': 'manager@acme.com',   'type': 'Approver'},
            {'name': 'Jane Borrower',  'email': 'jane@example.com',   'type': 'Signer',     'routingOrder': 1},
            {'name': 'John Seller',    'email': 'john@example.com',   'type': 'Signer',     'routingOrder': 2},
            {'name': 'Title Company',  'email': 'filing@titleco.com', 'type': 'CarbonCopy'},
        ]
    }
).raise_for_status()`,

        csharp: `var signers = new object[]
{
    new { name = "Loan Officer",   email = "officer@acme.com",   type = "Editor" },
    new { name = "Branch Manager", email = "manager@acme.com",   type = "Approver" },
    new { name = "Jane Borrower",  email = "jane@example.com",   type = "Signer",     routingOrder = 1 },
    new { name = "John Seller",    email = "john@example.com",   type = "Signer",     routingOrder = 2 },
    new { name = "Title Company",  email = "filing@titleco.com", type = "CarbonCopy" },
};

await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/bulk",
    new { signers });`,

        php: `$signers = [
    ['name' => 'Loan Officer',   'email' => 'officer@acme.com',   'type' => 'Editor'],
    ['name' => 'Branch Manager', 'email' => 'manager@acme.com',   'type' => 'Approver'],
    ['name' => 'Jane Borrower',  'email' => 'jane@example.com',   'type' => 'Signer',     'routingOrder' => 1],
    ['name' => 'John Seller',    'email' => 'john@example.com',   'type' => 'Signer',     'routingOrder' => 2],
    ['name' => 'Title Company',  'email' => 'filing@titleco.com', 'type' => 'CarbonCopy'],
];

$client->post("api/v1/packages/{$packageId}/signers/bulk", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['signers' => $signers],
]);`,

        ruby: `signers = [
  { name: 'Loan Officer',   email: 'officer@acme.com',   type: 'Editor' },
  { name: 'Branch Manager', email: 'manager@acme.com',   type: 'Approver' },
  { name: 'Jane Borrower',  email: 'jane@example.com',   type: 'Signer',     routingOrder: 1 },
  { name: 'John Seller',    email: 'john@example.com',   type: 'Signer',     routingOrder: 2 },
  { name: 'Title Company',  email: 'filing@titleco.com', type: 'CarbonCopy' }
]

uri = URI("${API}/api/v1/packages/#{package_id}/signers/bulk")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { signers: signers }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Template role with type ──────────────────────────────────────────────
    templateRole: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/roles \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "name": "Manager",
    "type": "Approver",
    "routingOrder": 1
  }'`,

        js: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Manager',
    type: 'Approver',
    routingOrder: 1
  })
});`,

        node: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Manager',
    type: 'Approver',
    routingOrder: 1
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/templates/{template_id}/roles',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Manager',
        'type': 'Approver',
        'routingOrder': 1
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/templates/{templateId}/roles",
    new
    {
        name = "Manager",
        type = "Approver",
        routingOrder = 1
    });`,

        php: `$client->post("api/v1/templates/{$templateId}/roles", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Manager',
        'type' => 'Approver',
        'routingOrder' => 1,
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}/roles")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Manager',
  type: 'Approver',
  routingOrder: 1
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    }
};

export default {
    name: 'GuideRecipientTypes',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
